import { useState, useLayoutEffect } from 'react'

type Dimensions = {
  width: number
  height: number
}

// Simple debounce function
function debounce(fn: () => void, ms: number) {
  let timer: NodeJS.Timeout

  return () => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      fn()
    }, ms)
  }
}

export const useDimensions = (ref: React.RefObject<HTMLElement>): Dimensions => {
  const [dimensions, setDimensions] = useState<Dimensions>({ width: 0, height: 0 })

  // Update dimensions function
  const updateDimensions = () => {
    if (ref.current) {
      setDimensions({
        width: ref.current.offsetWidth,
        height: ref.current.offsetHeight
      })
    }
  }

  useLayoutEffect(() => {
    const debouncedHandleResize = debounce(updateDimensions, 300) // Debounce the update function

    updateDimensions()  // Update dimensions on mount

    // Handle window resize
    window.addEventListener('resize', debouncedHandleResize)

    // Cleanup listener and debounced function on component unmount
    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
      clearTimeout(debouncedHandleResize as unknown as number) // Clear the timer when the component unmounts
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])  // Empty dependency array ensures the effect runs only on mount and unmount

  return dimensions
}
